import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AppShell, Button, SegmentedControl, ToolbarDivider } from '@cloudsam/ui';
import StatisticalSummary from './StatisticalSummary';
import DockablePanel from './common/DockablePanel';
import ExploreTab from './ExploreTab';
import ExplorerHeader from './ExplorerHeader';
import AnalysisTab from './AnalysisTab';
import SamLogo from './SamLogo';
import { openHelpAgent, notifyHelpAgent } from '../utils/openHelpAgent';
import { HelpCircle } from 'lucide-react';
import ArchiverPanel from './ArchiverPanel';
import DeploymentManagerPanel from './DeploymentManagerPanel';
import DesignTab from './DesignTab';
import { loadDeploymentResponses } from '../utils/explorerApi';
import { mapQuestionTypeToMatrixType } from '../utils/matrixTypes';
import './SurveyExplorer.css';

const PRIMARY_TABS = [['explore', 'Explore'], ['design', 'Design'], ['analysis', 'Analyze'], ['deployments', 'Deployments']];

/**
 * Format ISO timestamp for display in user's local timezone
 */
export function toLocalDisplay(isoString) {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString(undefined, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
}

// URL migration: old ?tab=charting|statistics|performance land on Analysis.
function getInitialTab() {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t === 'charting' || t === 'statistics' || t === 'performance') {
        return { tab: 'analysis', health: t === 'performance' };
    }
    if (t === 'explore' || t === 'analysis' || t === 'design' || t === 'deployments') {
        return { tab: t, health: false };
    }
    return { tab: 'explore', health: false };
}

/**
 * SurveyExplorer
 *
 * Main component for the Survey Explorer interface
 * Provides real-time analytics and visualization for survey responses
 *
 * @param {string} deploymentId - The deployment to analyze
 * @param {string} surveyName - Name of the survey
 * @param {Object} surveyStructure - Survey definition with questions/variables
 * @param {Function} onClose - Callback to close the explorer
 *
 * SurveyExplorer owns its response fetch and incremental polling — it calls
 * loadDeploymentResponses (which wraps /dbam/v1/explorer/responses) directly.
 * If surveyStructure has no questions, the explorer derives a fallback
 * variables list from response_data keys.
 */
function SurveyExplorerInner({
    surveyId,
    deploymentId,
    surveyName,
    surveyStructure,
    onClose,
    setSelectionRule,
    selectedDeployment,
    onRefreshDeployments,
    deploymentSelector,
    // WP-D3b: re-pull the survey structure after category adoption so the
    // Analysis surfaces see fresh response_categories.
    onRefreshStructure
}) {
    // State
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Bumped on every full re-pull (Refresh button or new-response signal). Read
    // by ExploreTab to re-fetch its session list + results rows without losing
    // the user's session selection.
    const [refreshNonce, setRefreshNonce] = useState(0);

    // Frozen-as-history: once the deployment is undeployed (is_open === false /
    // survey back to ready) we stop live updates and show the final values as
    // history. A deployment with no is_open field yet (still loading) is treated
    // as live.
    const isFrozen = selectedDeployment ? selectedDeployment.is_open === false : false;

    // Keep the latest onRefreshDeployments in a ref so the polling effect can
    // call it without listing it as a dependency (which would tear down and
    // rebuild the interval on every parent re-render).
    const onRefreshDeploymentsRef = useRef(onRefreshDeployments);
    useEffect(() => { onRefreshDeploymentsRef.current = onRefreshDeployments; }, [onRefreshDeployments]);

    const [launchParams] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            surveyId: urlParams.get('surveyId'),
            deploymentId: urlParams.get('deploymentId'),
            userId: urlParams.get('userId'),
        };
    });

    // Auto-track: tell the help popup we're in Explorer whenever this tab gets focus
    useEffect(() => {
        const notify = () => notifyHelpAgent('explorer', launchParams.userId);
        window.addEventListener('focus', notify);
        return () => window.removeEventListener('focus', notify);
    }, [launchParams.userId]);

    const [liveMessage, setLiveMessage] = useState('');
    const [activeTab, setActiveTab] = useState(() => getInitialTab().tab);
    const [healthPreselect, setHealthPreselect] = useState(() => getInitialTab().health); // eslint-disable-line no-unused-vars
    // Open-in-Analysis bridge (WP-C2): the Explore matrix sets a question name
    // here and switches to the Analyze tab; AnalysisTab consumes + clears it.
    const [analysisOpenQuestion, setAnalysisOpenQuestion] = useState(null);
    // A session to reveal in Explore, set when an Analysis surface links to the
    // recipient behind an answer. Carries a nonce so asking for the same
    // session twice still re-reveals it.
    const [exploreOpenSession, setExploreOpenSession] = useState(null);
    // The Analysis tab unmounts whenever another tab is shown, so the question
    // an author is reading is remembered here instead of inside it.
    const [analysisMemory, setAnalysisMemory] = useState(null);
    const currentSurveyId = surveyId || launchParams.surveyId;
    const currentDeploymentId = deploymentId || launchParams.deploymentId;

    // Track the max response date across all loaded responses for incremental polling
    const lastResponseDateRef = useRef(null);
    // ExploreTab registers its export handler here so the Tools ▾ "Export" entry
    // (rendered up in the command bar) can trigger an export that needs
    // ExploreTab's selection/view state. Null when the Explore tab is unmounted.
    const exploreExportRef = useRef(null);

    // Variables derived from survey structure (with fallback to data-derived)
    const [variables, setVariables] = useState([]);
    const [dataDerivedVariables, setDataDerivedVariables] = useState([]);
    const [archiverOpen, setArchiverOpen] = useState(false);

    // Keep the active top-level tab in the URL for shareable explorer state.
    useEffect(() => {
        const url = new URL(window.location);
        url.searchParams.set('tab', activeTab);
        window.history.replaceState({}, '', url);
    }, [activeTab]);

    useEffect(() => {
        if (currentDeploymentId) {
            const lsKey = launchParams.userId
                ? `console.explorer.lastDeployment.${launchParams.userId}`
                : 'console.explorer.lastDeployment';
            localStorage.setItem(lsKey, JSON.stringify({
                surveyId: currentSurveyId,
                deploymentId: currentDeploymentId,
            }));
        }
    }, [currentSurveyId, currentDeploymentId, launchParams.userId]);

    // Parse survey structure into variables list (with data-derived fallback)
    useEffect(() => {
        const vars = [];
        const structureQuestions = surveyStructure?.questions || [];

        if (structureQuestions.length > 0) {
            // Prefer the structure endpoint's questions
            structureQuestions.forEach(q => {
                // Charting must key on variable_name: the descriptor node id
                // (q.id) does NOT match the variable_name keys in response_data.
                // Skip questions without a variable_name — they'd become phantom
                // variables with null names that can never bind to a column.
                if (!q.variable_name) return;
                const questionType = q.type || 'text';
                const matrixType = q.matrixType || mapQuestionTypeToMatrixType(questionType);
                vars.push({
                    name: q.variable_name || q.id,
                    label: q.text || q.label,
                    type: questionType,
                    matrixType: matrixType,
                    options: q.options || [],
                    // WP-D3: the descriptor node id (classify qid) and authored
                    // response categories, threaded through for the classified
                    // free-text Analysis surface.
                    id: q.question_id || q.id,
                    question_number: q.question_number ?? q.number ?? q.display_id,
                    responseCategories: q.response_categories || []
                });
            });
        } else if (dataDerivedVariables.length > 0) {
            // Fallback when structure endpoint is empty — scan response_data keys
            dataDerivedVariables.forEach(v => vars.push({
                name: v.name,
                label: v.label,
                type: v.type,
                matrixType: v.matrixType,
                options: v.options || [],
            }));
        }

        // Two system variables were dropped from this list. Recipient Group
        // (A/B) predates Segments and is empty under the current architecture;
        // Completion Time duplicated the pinned "Time to complete" view, which
        // is the one that works.
        //
        // Response Date is not a variable either. When answers arrived is a
        // real question, but as a scalar it charted nothing: ScalarCanvas read
        // an ISO timestamp as a number and reported no numeric responses. It is
        // now the pinned "Responses over time" view, which bins by when each
        // take finished.
        setVariables(vars);
    }, [surveyStructure, dataDerivedVariables]);

    // Helper: Merge new responses into existing, deduplicating by session_id
    const mergeResponses = useCallback((existing, incoming) => {
        const map = new Map(existing.map(r => [r.session_id, r]));
        incoming.forEach(r => map.set(r.session_id, r));
        return Array.from(map.values());
    }, []);

    // Helper: Compute max response_date as ISO string for the `since` cursor
    const computeMaxDate = useCallback((rows) => {
        if (!rows.length) return null;
        const stamps = rows.map(r => r.response_date).filter(Boolean);
        if (!stamps.length) return null;
        return new Date(Math.max(...stamps.map(d => new Date(d).getTime()))).toISOString();
    }, []);

    // Manual refresh — full reload (no `since`). "Refresh means refresh": it
    // re-pulls the response rows/charts AND the enriched-deployment counters/n
    // (via onRefreshDeployments) AND the session list + results rows (via the
    // refreshNonce ExploreTab watches) — not just the chart rows.
    const handleManualRefresh = useCallback(async () => {
        if (!currentDeploymentId) return;
        try {
            setLoading(true);
            lastResponseDateRef.current = null;
            const { responses: rows, variables: derived } = await loadDeploymentResponses(currentDeploymentId);
            setResponses(rows);
            setDataDerivedVariables(derived);
            lastResponseDateRef.current = computeMaxDate(rows);
            setError('');
            onRefreshDeploymentsRef.current?.();
            setRefreshNonce(n => n + 1);
        } catch (err) {
            setError(err?.message || 'Failed to load responses');
        } finally {
            setLoading(false);
        }
    }, [currentDeploymentId, computeMaxDate]);

    // Initial load and incremental polling — owned by the explorer.
    useEffect(() => {
        if (!currentDeploymentId) return;
        let cancelled = false;

        // Initial full load
        const doFullLoad = async () => {
            try {
                setLoading(true);
                const { responses: rows, variables: derived } = await loadDeploymentResponses(currentDeploymentId);
                if (cancelled) return;
                setResponses(rows);
                setDataDerivedVariables(derived);
                lastResponseDateRef.current = computeMaxDate(rows);
                setError('');
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load responses');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        doFullLoad();

        // Frozen-as-history: a closed/undeployed deployment gets the full load
        // above (so its final values render) but no live polling.
        if (isFrozen) {
            return () => { cancelled = true; };
        }

        // Incremental polling every 5 seconds. The `since` cursor is the max
        // response_date across all loaded responses (not just the last poll's
        // batch) — guards against clock skew leaving gaps. See WP-SE-21.
        const interval = setInterval(async () => {
            // Keep the enriched-deployment counters / n / lifecycle labels live
            // and catch an undeploy (is_open → false) so the view can freeze.
            onRefreshDeploymentsRef.current?.();
            if (!lastResponseDateRef.current) return;
            try {
                const { responses: incoming } = await loadDeploymentResponses(
                    currentDeploymentId,
                    { since: lastResponseDateRef.current }
                );
                if (cancelled || !incoming.length) return;
                setResponses(prev => {
                    const merged = mergeResponses(prev, incoming);
                    lastResponseDateRef.current = computeMaxDate(merged);
                    return merged;
                });
                // The new-response signal triggers a full re-pull of the session
                // list and results rows (charts already read `responses`).
                setRefreshNonce(n => n + 1);
                setLiveMessage(`${incoming.length} new response${incoming.length !== 1 ? 's' : ''}`);
                setTimeout(() => setLiveMessage(''), 3000);
            } catch (err) {
                console.error('Incremental poll failed:', err);
            }
        }, 5000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [currentDeploymentId, isFrozen, mergeResponses, computeMaxDate]);

    return (
        <AppShell
            wordmark="Explorer"
            logo={<SamLogo size={36} variant="light" />}
            help={(
                <Button
                    variant="onDark"
                    onClick={() => openHelpAgent({ context: 'explorer', userId: launchParams.userId })}
                    title="Open Sam Help Agent"
                >
                    <HelpCircle size={16} strokeWidth={2.25} /> Help
                </Button>
            )}
            /* §6 item 2: view switching lives in the navy band, never on the
               content background. The ?tab= parameter and its deep links are
               unchanged — this swaps the control, not the routing.
               Left-aligned with the other command buttons rather than centred
               (§2.2 says centre; overridden on request 2026-08-05 for
               consistency with Console and Designer, whose controls all sit
               left). */
            /* §6 item 3: the band carries controls only. The History / Live
               readouts moved to the Context panel below. */
            toolbarLeft={(
                <>
                    <Button
                        variant="onDark"
                        onClick={handleManualRefresh}
                        disabled={loading || !currentDeploymentId}
                        title={!currentDeploymentId ? 'Select a survey and deployment first' : ''}
                    >
                        Refresh
                    </Button>
                    <ToolbarDivider />
                    <SegmentedControl
                        ariaLabel="Explorer views"
                        options={PRIMARY_TABS.map(([value, label]) => ({ value, label }))}
                        value={activeTab}
                        onChange={setActiveTab}
                    />
                    <ToolbarDivider />
                    <Button
                        variant="onDark"
                        active={archiverOpen}
                        onClick={() => setArchiverOpen(v => !v)}
                        title="Open the Archiver"
                    >
                        Archiver
                    </Button>
                </>
            )}
            /* Explorer's body is a horizontal splitter layout, not the standard
               stacked panel column — so the shell's padding and gap are cleared
               and the app keeps its own content flow. */
            /* §2.3: content region on --color-bg-base, outer padding and
               inter-panel gap at --spacing-md. That padding is what makes the
               base colour read as interstitial space around the panels. */
            contentStyle={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
            {/* Live region for screen reader announcements */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">{liveMessage}</div>

            {/* Error Display */}
            {error && (
                <div style={styles.error}>
                    {error}
                </div>
            )}


            <ExplorerHeader
                status={(
                    <>
                        {isFrozen && currentDeploymentId && (
                            <span style={styles.historyBadgeInline} title="This deployment is closed — showing final values as history">
                                History · closed
                            </span>
                        )}
                        {!isFrozen && liveMessage && (
                            <span style={styles.liveBadgeInline} aria-live="polite">
                                <span style={styles.liveDot} />
                                {liveMessage}
                            </span>
                        )}
                        {currentDeploymentId && (
                            <span style={styles.liveIndicator} title={isFrozen ? 'Deployment closed' : 'Deployment live'}>
                                <span style={{ ...styles.liveIndicatorDot, background: isFrozen ? '#F44336' : '#16a34a', animation: isFrozen ? 'none' : 'pulse 2s infinite' }} />
                                {isFrozen ? 'Closed' : 'Live'}
                            </span>
                        )}
                    </>
                )}
                deploymentSelector={deploymentSelector || (
                    <div style={styles.fallbackDeploymentSelector}>
                        <strong>Survey:</strong> {surveyName || 'Untitled Survey'}
                    </div>
                )}
                statisticalSummary={(
                    <StatisticalSummary
                        enrichedDeployment={selectedDeployment}
                        responses={responses}
                        onSelectionRuleChange={setSelectionRule}
                        loading={loading}
                    />
                )}
            />

            {/* The tab strip that used to sit here has moved into the toolbar
                band as a SegmentedControl (§6 item 2). */}

            {/* Main Content */}
            <div style={styles.content}>
                {(!currentSurveyId || !currentDeploymentId) ? (
                    // Empty / context-free state. Chrome (masthead, toolbar band,
                    // selector header, view switcher) stays mounted so the layout
                    // matches the populated view; the body shows a prompt instead
                    // of a tab. New and trial accounts hit this path until they
                    // create their first deployment.
                    <div style={styles.emptyStateContainer}>
                        <div style={styles.emptyStateCard}>
                            <SamLogo size={48} variant="onWhite" style={{ opacity: 0.3, marginBottom: 16 }} />
                            <h2 style={styles.emptyStateTitle}>Survey Explorer</h2>
                            <p style={styles.emptyStateText}>
                                Select a survey and deployment above to begin exploring results.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'explore' && (
                            <ExploreTab
                                deploymentId={currentDeploymentId}
                                surveyId={currentSurveyId}
                                surveyName={surveyName}
                                deploymentResponses={responses}
                                refreshNonce={refreshNonce}
                                exportHandlerRef={exploreExportRef}
                                enrichedDeployment={selectedDeployment}
                                onOpenInAnalysis={(name) => { setAnalysisOpenQuestion(name); setActiveTab('analysis'); }}
                                openSession={exploreOpenSession}
                                onSessionOpened={() => setExploreOpenSession(null)}
                            />
                        )}

                        {activeTab === 'analysis' && (
                            <AnalysisTab
                                variables={variables}
                                responses={responses}
                                loading={loading}
                                deploymentId={currentDeploymentId}
                                healthPreselect={healthPreselect}
                                openQuestion={analysisOpenQuestion}
                                onOpenSession={(sessionId) => { setExploreOpenSession({ sessionId, nonce: Date.now() }); setActiveTab('explore'); }}
                                analysisMemory={analysisMemory}
                                onAnalysisMemoryChange={setAnalysisMemory}
                                onQuestionOpened={() => setAnalysisOpenQuestion(null)}
                                onStructureRefresh={onRefreshStructure}
                            />
                        )}
                        {activeTab === 'design' && <DesignTab deploymentId={currentDeploymentId} />}

                        {activeTab === 'deployments' && (
                            <div style={styles.deploymentsTabWrap}>
                                <DeploymentManagerPanel
                                    surveyId={currentSurveyId}
                                    onClose={() => setActiveTab('explore')}
                                />
                            </div>
                        )}
                    </>
                )}

            </div>

            {archiverOpen && (
                <DockablePanel
                    title="Results Archiver"
                    defaultFloating
                    defaultWidth={920}
                    defaultHeight={620}
                    defaultPosition={{ x: 100, y: 160 }}
                    storageKey="explorer.archiver"
                    onClose={() => setArchiverOpen(false)}
                >
                    <ArchiverPanel
                        onClose={() => setArchiverOpen(false)}
                    />
                </DockablePanel>
            )}
        </AppShell>
    );
}

export default function SurveyExplorer(props) {
    return <SurveyExplorerInner {...props} />;
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: '#ffffff',
    },
    logoBar: {
        background: 'var(--color-header-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 16px',
        borderBottom: '1px solid #152d4a',
        flexShrink: 0,
    },
    wordmark: {
        color: 'var(--color-bg-base)',
        fontWeight: 'var(--font-weight-regular)',
        letterSpacing: '4px',
        fontSize: 'var(--font-size-md)',
        textTransform: 'uppercase',
        fontFamily: 'var(--font-family-primary)',
        whiteSpace: 'nowrap',
        marginLeft: 12,
    },
    commandBar: {
        background: 'var(--color-header-bg)',
        borderBottom: '1px solid #152d4a',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-sm) var(--spacing-base)',
        flexShrink: 0,
    },
    commandButton: {
        background: 'var(--color-bg-panel)',
        color: 'var(--color-neutral-700)',
        border: '1px solid var(--color-border-medium)',
        borderRadius: 'var(--radius-base)',
        padding: 'var(--spacing-sm) var(--spacing-base)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-family-primary)',
    },
    commandButtonActive: {
        background: '#3b82f6',
        color: '#ffffff',
        borderColor: '#3b82f6',
    },
    /* These readouts sit in the Context panel head (--color-surface-100, a light
       tint) since §6 item 3 moved them off the navy band. They keep the panel's
       own ink colours — the pale on-navy palette they used to carry read as
       near-invisible white-on-white here. */
    liveBadgeInline: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-xs)',
        color: 'var(--color-text-primary)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
    },
    historyBadgeInline: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-xs)',
        padding: '2px 10px',
        borderRadius: 12,
        background: 'rgba(32, 33, 36, 0.08)',
        color: 'var(--color-text-primary)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        letterSpacing: '0.3px',
    },
    header: {
        background: '#ffffff',
        borderBottom: '2px solid #e5e7eb',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: '#1f2937',
    },
    surveyInfo: {
        fontSize: 14,
        color: '#666',
    },
    liveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        background: '#dcfce7',
        color: '#16a34a',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
    },
    liveUpdate: {
        fontWeight: 400,
        marginLeft: 2,
    },
    liveDot: {
        width: 8,
        height: 8,
        background: '#16a34a',
        borderRadius: '50%',
        animation: 'pulse 2s infinite',
    },
    responseCounter: {
        fontSize: 14,
        color: '#666',
        padding: '6px 12px',
        background: '#f3f4f6',
        borderRadius: 6,
    },
    responseCountNumber: {
        color: '#3b82f6',
        fontSize: 16,
    },
    performanceButton: {
        background: 'transparent',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 6,
        fontSize: 13,
        cursor: 'pointer',
        padding: '6px 12px',
        color: '#3b82f6',
        transition: 'all 0.2s',
    },
    performanceButtonActive: {
        background: '#3b82f6',
        color: 'white',
        borderColor: '#3b82f6',
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: 20,
        cursor: 'pointer',
        padding: '4px 8px',
        color: '#6b7280',
    },
    error: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        padding: 12,
        margin: '8px 16px',
        borderRadius: 6,
        fontSize: 13,
    },
    fallbackDeploymentSelector: {
        fontSize: 13,
        color: '#3C4043',
    },
    content: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
    },
    emptyStateContainer: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg-base)',
    },
    emptyStateCard: {
        background: '#FFFFFF',
        borderRadius: 6,
        padding: 40,
        maxWidth: 500,
        border: '1px solid #E8EAED',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
    },
    emptyStateTitle: {
        margin: '0 0 8px',
        color: '#3C4043',
        fontFamily: 'var(--font-family-primary)',
    },
    emptyStateText: {
        color: '#5F6368',
        fontFamily: 'var(--font-family-primary)',
        margin: 0,
    },
    tabs: {
        display: 'flex',
        gap: 0,
        borderBottom: '2px solid #E8EAED',
        padding: '0 16px',
        flexShrink: 0,
        background: '#ffffff',
    },
    tabButton: {
        padding: '10px 16px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 400,
        color: '#5F6368',
        borderBottom: '2px solid transparent',
        marginBottom: -2,
    },
    tabButtonActive: {
        fontWeight: 600,
        color: '#1967D2',
        borderBottom: '2px solid #1967D2',
    },
    liveIndicator: { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', padding: '2px 10px' },
    liveIndicatorDot: { width: 8, height: 8, borderRadius: '50%' },
    deploymentsTabWrap: { flex: 1, overflow: 'auto', padding: 16, background: 'var(--color-bg-base)' },
};
